import TradeSubmittedModal from 'components/core-v2/organisms/trade-submitted-modal'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Icon,
  IconButton,
  Section,
  Space,
  TextInput,
  Typography,
  ValidationMessage
} from '@/components/core-v2'
import { PATH } from '@/const'
import { UserDisplayInfo } from '@/types/user'

import { actions as builderActions } from '../../../tradebuilder-v2/modules/00-builder/state/builder-slice'
import { useAppDispatch, useAppSelector } from '../../../tradebuilder-v2/store/hooks'
import { TradeActivationStep } from '../../../tradebuilder-v2/types'
import { Errors } from '../../../tradebuilder-v2/util'
import { StyledList, StyledMaxWidthArea } from './trade-close-page.styles'

type TradeClosePageProps = {
  accountId?: string
  closeAll?: boolean
  userInfo: UserDisplayInfo
}

export function TradeClosePage({ accountId, closeAll, userInfo }: TradeClosePageProps) {
  const navigate = useNavigate()
  const [enteredUserName, setEnteredUserName] = useState<string>('')
  const [isActiveTrade, setIsActiveTrade] = useState<boolean>(false)
  const [canClose, setCanClose] = useState<boolean>(false)
  const [errors, setErrors] = useState<Errors>({})
  const dispatch = useAppDispatch()
  const builderState = useAppSelector(state => state.builder)

  useEffect(() => {
    dispatch(
      builderActions.init({
        accountId,
        userName: userInfo.userName,
        userRoles: userInfo.userRoles,
        userType: userInfo.userType
      })
    )
  }, [dispatch, accountId, userInfo.userName, userInfo.userRoles, userInfo.userType])
  useEffect(() => {
    const builderErrors: Errors = builderState.errors
    if (builderState.status) {
      const isActiveTrade = builderState.status !== TradeActivationStep.inactive
      setIsActiveTrade(isActiveTrade)
      if (!isActiveTrade) {
        builderErrors.Inactive = 'Trade is already closed and waiting for withdrawal of funds'
      }
    }
    setErrors(builderErrors)
  }, [builderState.status, builderState.errors])

  useEffect(() => {
    if (builderState.isFinished) {
      if (closeAll) {
        dispatch(
          builderActions.setApprovalNotification({
            title: 'Trades closed',
            description: `All trades in ${builderState.note.noteName} will be submitted for approval to be closed`
          })
        )
      } else {
        dispatch(
          builderActions.setApprovalNotification({
            title: 'Trade closed',
            description: `Trade ${builderState.selectedAccount.accountName} will be submitted for approval to be closed`
          })
        )
      }
    }
  }, [
    builderState.isFinished,
    builderState.note?.noteName,
    builderState.selectedAccount?.accountName,
    dispatch,
    closeAll
  ])

  const onChange = args => {
    const enteredUserName = args.target.value
    setEnteredUserName(enteredUserName)
    setCanClose(isActiveTrade && enteredUserName === userInfo?.userName)
  }
  const onClose = () => {
    if (closeAll) {
      dispatch(builderActions.closeAllTradesInNote())
    } else {
      dispatch(builderActions.closeTrade())
    }
  }
  const onNotificationClose = () => {
    dispatch(builderActions.resetNotification())
    navigate(PATH.trades)
  }
  return (
    <Section py={6} type="quaternary">
      <StyledMaxWidthArea>
        <Section type="primary">
          <Space isflex flexDirection="column" justifySelf="center" px={5} py={6}>
            {!closeAll && (
              <>
                <Space pb={6}>
                  <Typography variant="h5">Close the trade</Typography>
                </Space>
                <Space pb={6}>
                  <Typography variant="b3">
                    By proceeding with this instruction to close the trade the following will occur:
                  </Typography>
                </Space>
                <Space pb={5}>
                  <StyledList>
                    <li>All holdings inside the trade will be sold.</li>
                    <li>
                      All proceeds of the sale of the holdings (after deductions to cover any
                      outstanding fees and loan payments) will be repaid to the holder of the note.
                    </li>
                    <li>The trade will no longer form part of the note.</li>
                  </StyledList>
                </Space>
              </>
            )}
            {closeAll && (
              <>
                <Space pb={6}>
                  <Typography variant="h5">Close all trades in this note</Typography>
                </Space>
                <Space pb={6}>
                  <Typography variant="b3">
                    By proceeding with this instruction to close the trades in this note the
                    following will occur:
                  </Typography>
                </Space>
                <Space pb={5}>
                  <StyledList>
                    <li>All holdings inside all trades in this note will be sold.</li>
                    <li>
                      All proceeds of the sale of the holdings (after deductions to cover any
                      outstanding fees and loan payments) will be repaid to the holder of the note.
                    </li>
                    <li>
                      The note will be redeemed and no longer available to make contributions to.
                    </li>
                  </StyledList>
                </Space>
              </>
            )}
            <Space pb={6}>
              <Typography variant="b3">Please consider carefully if you wish to proceed</Typography>
            </Space>
            <Space pb={5}>
              <Typography variant="b3">
                Please enter your username: {`${userInfo?.userName}`} and click on the button
              </Typography>
            </Space>
            <TextInput data-private value={enteredUserName} onChange={onChange} />
            <Space mb={5}>
              <ValidationMessage errors={errors} />
            </Space>
            <IconButton
              disabled={!canClose}
              icon={<Icon name="leave" size="default" />}
              iconPosition="left"
              operationType="progress"
              text="Close trade"
              onClick={onClose}
            />
          </Space>
        </Section>
      </StyledMaxWidthArea>
      {builderState && builderState.approvalNotification && (
        <TradeSubmittedModal
          acceptCaption="Go to all draft trades"
          open={builderState.approvalNotification !== undefined}
          steps={['Submit', 'Commitment', 'Activate']}
          onAccept={() => onNotificationClose()}
        />
      )}
    </Section>
  )
}
